<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <ul class="nav nav-pills user-report-tabs">
          <li class="nav-item" v-for="tab in tabs" :key="tab.key">
            <button
              type="button"
              class="nav-link"
              :class="{ active: activeTab === tab.key }"
              @click="$emit('tab-change', tab.key)"
            >
              {{ tab.label }}
            </button>
          </li>
        </ul>

        <div class="d-flex flex-wrap align-items-center gap-2">
          <span class="summary-chip">
            {{ summary.label }}: ${{ formatAmount(summary.total) }}
          </span>
          <template v-if="activeTab !== 'payment'">
            <span class="summary-chip chip-success">
              {{ $t(`${i18nScope}.summary_paid`) }}: ${{ formatAmount(summary.paid) }}
            </span>
            <span class="summary-chip chip-danger">
              {{ $t(`${i18nScope}.summary_due`) }}: ${{ formatAmount(summary.due) }}
            </span>
          </template>
        </div>
      </div>

      <a-table
        :columns="currentColumns"
        :data-source="items"
        :row-key="rowKey"
        :loading="loading"
        :pagination="pagination"
        :scroll="{ x: 1100 }"
        class="text-nowrap align-middle user-report-table"
        @change="handleChange"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">{{ emptyText }}</div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'date'">
            {{ formatDate(record.created_at) }}
          </template>

          <template v-else-if="column && column.key === 'status'">
            <span class="status-badge" :class="getStatusClass(record.status)">
              {{ formatLabel(record.status) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'invoice_total'">
            ${{ formatAmount(record.invoice_total) }}
          </template>

          <template v-else-if="column && column.key === 'paid'">
            ${{ formatAmount(record.paid) }}
          </template>

          <template v-else-if="column && column.key === 'due'">
            ${{ formatAmount(record.due) }}
          </template>

          <template v-else-if="column && column.key === 'payment_status'">
            <span class="status-badge" :class="getPaymentStatusClass(record)">
              {{ getPaymentStatus(record) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'amount'">
            ${{ formatAmount(record.amount) }}
          </template>

          <template v-else-if="column && column.key === 'creator'">
            {{ formatCreator(record) }}
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { TablePaginationConfig } from "ant-design-vue";

type UserReportTab = "sale" | "payment" | "quotation";
type UserReportRow = Record<string, unknown>;

export default defineComponent({
  name: "UserReport",
  props: {
    activeTab: {
      type: String as () => UserReportTab,
      required: true,
    },
    items: {
      type: Array as () => UserReportRow[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object as () => TablePaginationConfig,
      required: true,
    },
    summary: {
      type: Object as () => { label: string; total: number; paid?: number; due?: number },
      required: true,
    },
    i18nScope: {
      type: String,
      default: "user_report_page",
    },
  },
  emits: ["tab-change", "table-change"],
  setup(props, { emit }) {
    const { t, locale } = useI18n();

    const tabs = computed<Array<{ key: UserReportTab; label: string }>>(() => [
      { key: "sale", label: t(`${props.i18nScope}.tab_sales`) },
      { key: "payment", label: t(`${props.i18nScope}.tab_payments`) },
      { key: "quotation", label: t(`${props.i18nScope}.tab_quotations`) },
    ]);

    const saleColumns = computed(() => [
      { title: t(`${props.i18nScope}.table_date`), key: "date", dataIndex: "created_at", width: 210, align: "center" as const },
      { title: t(`${props.i18nScope}.table_reference`), key: "reference_no", dataIndex: "reference_no", width: 160, align: "center" as const },
      { title: t(`${props.i18nScope}.table_customer`), key: "customer_name", dataIndex: "customer_name", width: 200, align: "center" as const },
      { title: t(`${props.i18nScope}.table_created_by`), key: "creator", width: 180, align: "center" as const },
      { title: t(`${props.i18nScope}.table_quantity`), key: "quantity", dataIndex: "quantity", width: 110, align: "center" as const },
      { title: t(`${props.i18nScope}.table_status`), key: "status", dataIndex: "status", width: 140, align: "center" as const },
      { title: t(`${props.i18nScope}.table_grand_total`), key: "invoice_total", dataIndex: "invoice_total", width: 150, align: "center" as const },
      { title: t(`${props.i18nScope}.table_paid`), key: "paid", dataIndex: "paid", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_due`), key: "due", dataIndex: "due", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_payment_status`), key: "payment_status", width: 170, align: "center" as const },
    ]);

    const paymentColumns = computed(() => [
      { title: t(`${props.i18nScope}.table_date`), key: "date", dataIndex: "created_at", width: 210, align: "center" as const },
      { title: t(`${props.i18nScope}.table_payment_ref`), key: "payment_ref", dataIndex: "payment_ref", width: 160, align: "center" as const },
      { title: t(`${props.i18nScope}.table_sale_ref`), key: "sale_ref", dataIndex: "sale_ref", width: 160, align: "center" as const },
      { title: t(`${props.i18nScope}.table_customer`), key: "customer_name", dataIndex: "customer_name", width: 200, align: "center" as const },
      { title: t(`${props.i18nScope}.table_created_by`), key: "creator", width: 180, align: "center" as const },
      { title: t(`${props.i18nScope}.table_amount`), key: "amount", dataIndex: "amount", width: 140, align: "center" as const },
      { title: t(`${props.i18nScope}.table_paid_by`), key: "paid_by", dataIndex: "paid_by", width: 160, align: "center" as const },
    ]);

    const quotationColumns = computed(() => [
      { title: t(`${props.i18nScope}.table_date`), key: "date", dataIndex: "created_at", width: 210, align: "center" as const },
      { title: t(`${props.i18nScope}.table_reference`), key: "reference_no", dataIndex: "reference_no", width: 160, align: "center" as const },
      { title: t(`${props.i18nScope}.table_customer`), key: "customer_name", dataIndex: "customer_name", width: 200, align: "center" as const },
      { title: t(`${props.i18nScope}.table_created_by`), key: "creator", width: 180, align: "center" as const },
      { title: t(`${props.i18nScope}.table_quantity`), key: "quantity", dataIndex: "quantity", width: 110, align: "center" as const },
      { title: t(`${props.i18nScope}.table_status`), key: "status", dataIndex: "status", width: 140, align: "center" as const },
      { title: t(`${props.i18nScope}.table_grand_total`), key: "invoice_total", dataIndex: "invoice_total", width: 150, align: "center" as const },
      { title: t(`${props.i18nScope}.table_paid`), key: "paid", dataIndex: "paid", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_due`), key: "due", dataIndex: "due", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_payment_status`), key: "payment_status", width: 170, align: "center" as const },
    ]);

    const currentColumns = computed(() => {
      if (props.activeTab === "payment") return paymentColumns.value;
      if (props.activeTab === "quotation") return quotationColumns.value;
      return saleColumns.value;
    });

    const rowKey = computed(() => {
      if (props.activeTab === "payment") return "payment_id";
      if (props.activeTab === "quotation") return "quote_id";
      return "sale_id";
    });

    const emptyText = computed(() => {
      if (props.activeTab === "payment") return t(`${props.i18nScope}.no_payments_found`);
      if (props.activeTab === "quotation") return t(`${props.i18nScope}.no_quotations_found`);
      return t(`${props.i18nScope}.no_sales_found`);
    });

    const formatDate = (dateString?: string) => {
      if (!dateString) return t(`${props.i18nScope}.not_available`);
      return new Date(dateString).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const formatAmount = (value?: string | number) =>
      Number(value ?? 0).toLocaleString(locale.value === "km" ? "km-KH" : "en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    const formatLabel = (value?: string | number) => {
      const text = String(value ?? "pending").replace(/_/g, " ").toLowerCase();
      const normalizedKey = text.replace(/\s+/g, "_");
      const translationKey = `${props.i18nScope}.status_${normalizedKey}`;
      const translated = t(translationKey);
      if (translated !== translationKey) return translated;

      return text.charAt(0).toUpperCase() + text.slice(1);
    };
    const formatCreator = (record: UserReportRow) => {
      const full = String(record.creator_full_name ?? "").trim();
      if (full) return full;
      if (record.creator_username) return String(record.creator_username);
      if (record.created_by) return String(record.created_by);
      return t(`${props.i18nScope}.not_available_short`);
    };
    const getPaymentStatusKey = (record: UserReportRow) => {
      const paid = Number(record.paid ?? 0);
      const total = Number(record.invoice_total ?? 0);
      if (total > 0 && paid >= total) return "paid";
      if (paid > 0) return "partial";
      return "due";
    };
    const getPaymentStatus = (record: UserReportRow) => {
      return t(`${props.i18nScope}.status_${getPaymentStatusKey(record)}`);
    };
    const getStatusClass = (value?: string | number) => {
      const normalized = String(value ?? "pending").toLowerCase();
      if (["completed", "received", "approved", "sent"].includes(normalized)) return "status-success";
      if (["pending", "ordered", "draft"].includes(normalized)) return "status-warning";
      if (["cancelled", "rejected"].includes(normalized)) return "status-danger";
      return "status-neutral";
    };
    const getPaymentStatusClass = (record: UserReportRow) => {
      const status = getPaymentStatusKey(record);
      if (status === "paid") return "status-success";
      if (status === "partial") return "status-warning";
      return "status-danger";
    };

    const handleChange = (pagination: TablePaginationConfig) => {
      emit("table-change", pagination.current || 1, pagination.pageSize || 10);
    };

    return {
      tabs,
      currentColumns,
      rowKey,
      emptyText,
      formatDate,
      formatAmount,
      formatLabel,
      formatCreator,
      getPaymentStatus,
      getStatusClass,
      getPaymentStatusClass,
      handleChange,
    };
  },
});
</script>

<style scoped>
.user-report-tabs {
  gap: 10px;
}

.user-report-tabs .nav-link {
  border: 1px solid #e6ebf2;
  border-radius: 999px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #5b6780;
  background: #f8fafc;
}

.user-report-tabs .nav-link.active {
  border-color: #5d5fef;
  background: rgba(93, 95, 239, 0.12);
  color: #5d5fef;
}

.summary-chip {
  display: inline-flex;
  align-items: center;
  padding: 10px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #5d5fef;
  background: rgba(93, 95, 239, 0.1);
}

.chip-success {
  color: #1f9254;
  background: #e8f7ee;
}

.chip-danger {
  color: #d33c43;
  background: #fdecec;
}

.user-report-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.user-report-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-success {
  color: #1f9254;
  background: #e8f7ee;
}

.status-warning {
  color: #d48a00;
  background: #fff4db;
}

.status-danger {
  color: #d33c43;
  background: #fdecec;
}

.status-neutral {
  color: #61748a;
  background: #eef2f7;
}
</style>
